import type { AnalysisResults } from '../../services/api/novel-analysis';

type ParseFn = (str: string | null) => unknown;

export function PerspectivesTab({ perspectives, parseJson }: { perspectives: AnalysisResults['perspectives']; parseJson: ParseFn }) {
  // Group by chapter
  const grouped = perspectives.reduce((acc, p) => {
    const key = p.chapter_index;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {} as Record<number, typeof perspectives>);

  return (
    <div className="divide-y">
      {Object.entries(grouped).map(([chIdx, chPerspectives]) => (
        <div key={chIdx} className="p-4">
          <h3 className="font-medium text-sm mb-3">第 {chIdx} 章</h3>
          <div className="space-y-4">
            {chPerspectives.map((p) => {
              const physicalAttraction = parseJson(p.physical_attraction) as { level?: number; evidence?: string } | null;
              const emotionalState = parseJson(p.emotional_state) as { type?: string; evidence?: string } | null;
              const powerDynamic = parseJson(p.power_dynamic) as { status?: string; evidence?: string; shifts?: string[] } | null;
              const charmDisplayed = parseJson(p.charm_displayed) as { type?: string; manifestations?: string[]; contrast_effect?: string } | null;
              const relPatterns = parseJson(p.relationship_patterns) as string[] | string | null;
              const tensions = parseJson(p.emotional_tensions) as string[] | string | null;
              const quotes = parseJson(p.key_quotes) as string[] | null;
              const evidence = parseJson(p.evidence) as Record<string, string> | string[] | null;
              const insights = parseJson(p.insights) as string[] | string | null;

              return (
                <div key={p.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {/* Header: observer → subject */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-blue-700">{p.observer_name}</span>
                    <span className="text-gray-400 text-xs">觀察</span>
                    <span className="font-medium text-sm text-pink-700">{p.subject_name}</span>
                    {p.charm_type && (
                      <span className="px-2 py-0.5 bg-pink-50 text-pink-600 rounded text-xs ml-auto">{p.charm_type}</span>
                    )}
                  </div>

                  {/* Physical Attraction */}
                  {physicalAttraction && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">身體吸引力：</span>
                      {physicalAttraction.level != null && (
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <div key={i} className={`w-3 h-3 rounded-full ${i < physicalAttraction.level! ? 'bg-pink-400' : 'bg-gray-200'}`} />
                          ))}
                        </div>
                      )}
                      {physicalAttraction.evidence && <span className="text-xs text-gray-500">{physicalAttraction.evidence}</span>}
                    </div>
                  )}

                  {/* Emotional State */}
                  {emotionalState && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">情感狀態：</span>
                      {emotionalState.type && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{emotionalState.type}</span>}
                      {emotionalState.evidence && <span className="text-xs text-gray-500 ml-1">{emotionalState.evidence}</span>}
                    </div>
                  )}

                  {/* Power Dynamic */}
                  {powerDynamic && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">權力動態：</span>
                      {powerDynamic.status && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{powerDynamic.status}</span>}
                      {powerDynamic.evidence && <span className="text-xs text-gray-500 ml-1">{powerDynamic.evidence}</span>}
                      {powerDynamic.shifts?.map((s, i) => (
                        <div key={i} className="text-xs text-gray-400 pl-3 mt-0.5">↳ {s}</div>
                      ))}
                    </div>
                  )}

                  {/* Charm Displayed */}
                  {charmDisplayed && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">魅力展現：</span>
                      {charmDisplayed.type && <span className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded text-xs">{charmDisplayed.type}</span>}
                      {charmDisplayed.manifestations?.map((m, i) => (
                        <span key={i} className="text-xs text-gray-600 ml-1">· {m}</span>
                      ))}
                      {charmDisplayed.contrast_effect && (
                        <p className="text-xs text-gray-400 mt-0.5">反差效果：{charmDisplayed.contrast_effect}</p>
                      )}
                    </div>
                  )}

                  {/* Relationship Patterns */}
                  {relPatterns && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">關係模式：</span>
                      {Array.isArray(relPatterns)
                        ? relPatterns.map((r, i) => <span key={i} className="text-xs text-gray-600 ml-1">· {r}</span>)
                        : <span className="text-xs text-gray-600 ml-1">{relPatterns}</span>
                      }
                    </div>
                  )}

                  {/* Emotional Tensions */}
                  {tensions && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">情感張力：</span>
                      {Array.isArray(tensions)
                        ? tensions.map((t, i) => <span key={i} className="text-xs text-gray-600 ml-1">· {t}</span>)
                        : <span className="text-xs text-gray-600 ml-1">{tensions}</span>
                      }
                    </div>
                  )}

                  {/* Key Quotes */}
                  {quotes && quotes.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-500">關鍵引用：</span>
                      {quotes.map((q, i) => (
                        <blockquote key={i} className="text-sm text-gray-700 italic border-l-2 border-gray-300 pl-2 mt-1">
                          「{q}」
                        </blockquote>
                      ))}
                    </div>
                  )}

                  {/* Plot Context */}
                  {p.plot_context && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">劇情脈絡：</span>
                      <span className="text-xs text-gray-600">{p.plot_context}</span>
                    </div>
                  )}

                  {/* Evidence */}
                  {evidence && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">證據：</span>
                      {Array.isArray(evidence)
                        ? evidence.map((e, i) => <div key={i} className="text-xs text-gray-500 pl-3">· {e}</div>)
                        : Object.entries(evidence).map(([k, v]) => (
                            <div key={k} className="text-xs text-gray-500 pl-3">{k}：{v}</div>
                          ))
                      }
                    </div>
                  )}

                  {/* Insights */}
                  {insights && (
                    <div className="text-sm">
                      <span className="text-xs text-gray-500">洞察：</span>
                      {Array.isArray(insights)
                        ? insights.map((ins, i) => <div key={i} className="text-xs text-gray-600 pl-3">· {ins}</div>)
                        : <span className="text-xs text-gray-600 ml-1">{insights}</span>
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {perspectives.length === 0 && <p className="p-4 text-gray-500 text-sm">尚無視角分析資料</p>}
    </div>
  );
}
